import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocText,
  DocumentModel,
} from "@/lib/tool-kit/document";
import {
  asClause,
  fillFrame,
  indefiniteArticle,
  joinSeries,
  properWords,
  stripEnd,
  tidy,
} from "@/lib/tool-kit/essay/sentence";
import { parseTones } from "@/lib/tool-kit/essay/soapstone";
import {
  leadInItem,
  paragraphsOrPrompt,
  splitItems,
  splitLeadIn,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * TPCASTT — the seven-step poetry analysis routine taught in AP English classrooms:
 * Title, Paraphrase, Connotation, Attitude, Shifts, Title (again), Theme.
 *
 * The routine is a reading order, not a formula: each step feeds the next, and the
 * second look at the title only pays off after the poem has been read. This module
 * turns a student's notes into a worksheet-shaped document, keeping every prompt visible
 * while a step is empty so the blank form is already a usable printable.
 */

export type TpcasttStep =
  | "title"
  | "paraphrase"
  | "connotation"
  | "attitude"
  | "shifts"
  | "titleAgain"
  | "theme";

export const TPCASTT_STEPS: readonly TpcasttStep[] = [
  "title",
  "paraphrase",
  "connotation",
  "attitude",
  "shifts",
  "titleAgain",
  "theme",
];

export interface TpcasttInput {
  /** The poem's title as printed. */
  title: string;
  poet: string;
  /** Step T: what the title leads you to expect, written before reading. */
  predict: string;
  paraphrase: string;
  /** One "word or image — what it suggests" per line. */
  connotation: string;
  /** Poetic devices noticed, as a list: "personification, enjambment". */
  devices: string;
  /** Tone words for the speaker's attitude. */
  attitude: string;
  attitudeEvidence: string;
  /** One shift per line: "lines 9–14 — from … to …". */
  shifts: string;
  titleAgain: string;
  theme: string;
}

export const TPCASTT_FIELDS: readonly (keyof TpcasttInput)[] = [
  "title",
  "poet",
  "predict",
  "paraphrase",
  "connotation",
  "devices",
  "attitude",
  "attitudeEvidence",
  "shifts",
  "titleAgain",
  "theme",
];

export const EMPTY_TPCASTT: TpcasttInput = {
  title: "",
  poet: "",
  predict: "",
  paraphrase: "",
  connotation: "",
  devices: "",
  attitude: "",
  attitudeEvidence: "",
  shifts: "",
  titleAgain: "",
  theme: "",
};

export interface TpcasttStepLabels {
  /** "T — Title (before you read)". */
  heading: string;
  /** The question the step asks, shown as guidance. */
  question: string;
  /** What to write while the step is empty. */
  prompt: string;
}

export interface TpcasttLabels {
  /** "TPCASTT: “{title}”". */
  docTitle: string;
  docTitleBlank: string;
  /** "by {poet} · seven-step poetry analysis". */
  subtitle: string;
  subtitleBlank: string;
  steps: Readonly<Record<TpcasttStep, TpcasttStepLabels>>;
  toneLabel: string;
  and: string;
  thesisHeading: string;
  thesisGuidance: string;
  thesisPrompt: string;
  /** Bracketed stand-ins for anything not filled in. */
  missing: Readonly<Record<string, string>>;
  /** Translated thesis frames; the English frames when omitted. */
  thesisFrames?: readonly string[];
}

export interface TpcasttOptions {
  /** Add ruled lines under each step that is still empty — for handwriting. */
  ruledLines: boolean;
}

/** How many ruled lines an empty step gets on the printed worksheet. */
const RULED: Record<TpcasttStep, number> = {
  title: 2,
  paraphrase: 6,
  connotation: 4,
  attitude: 3,
  shifts: 3,
  titleAgain: 2,
  theme: 2,
};

/**
 * Thesis frames. `{aTone}` carries its article ("a wistful"), `{devices}` is the list of
 * devices, `{shift}` the first shift the student noted.
 */
export const TPCASTT_THESIS_FRAMES = [
  "In “{title}”, {poet} uses {devices} to convey {aTone} attitude, suggesting that {theme}.",
  "“{title}” turns on a shift — {shift} — that leads the reader to see that {theme}.",
] as const;

function orMissing(
  value: string,
  key: string,
  missing: Readonly<Record<string, string>>
): string {
  return value === "" ? (missing[key] ?? `[${key}]`) : value;
}

/** The first shift the student wrote, without its leading "lines 9–14 —". */
export function firstShift(shifts: string): string {
  const first = splitItems(shifts)[0] ?? "";
  const parts = splitLeadIn(first);
  return stripEnd(parts ? parts[1] : first);
}

/** Each thesis frame, filled; empty fields show as bracketed stand-ins. */
export function buildTpcasttThesis(
  input: TpcasttInput,
  labels: Pick<TpcasttLabels, "missing" | "and">,
  frames: readonly string[] = TPCASTT_THESIS_FRAMES
): string[] {
  const proper = properWords(Object.values(input));
  const tones = parseTones(input.attitude);
  const toneText = orMissing(
    joinSeries(tones, labels.and),
    "tone",
    labels.missing
  );
  const devices = joinSeries(
    parseTones(input.devices).filter((device) => device !== ""),
    labels.and
  );
  const values: Record<string, string> = {
    title: orMissing(stripEnd(input.title), "title", labels.missing),
    poet: orMissing(stripEnd(input.poet), "poet", labels.missing),
    devices: orMissing(devices, "devices", labels.missing),
    aTone: `${indefiniteArticle(toneText)} ${toneText}`,
    shift: orMissing(firstShift(input.shifts), "shift", labels.missing),
    theme: orMissing(asClause(input.theme, proper), "theme", labels.missing),
  };
  return frames
    .map((frame) => fillFrame(frame, values))
    .filter((text): text is string => text !== null);
}

function stepSection(
  step: TpcasttStep,
  labels: TpcasttLabels,
  blocks: DocBlock[],
  filled: boolean,
  options: TpcasttOptions
): DocSection {
  const copy = labels.steps[step];
  const all: DocBlock[] = [
    { kind: "guidance", text: copy.question },
    ...blocks,
  ];
  if (options.ruledLines && !filled) {
    all.push({ kind: "lines", count: RULED[step] });
  }
  return { heading: copy.heading, blocks: all };
}

function listBlocks(
  text: string,
  prompt: DocText,
  withLeadIn: boolean,
  style: "bullet" | "decimal"
): DocBlock[] {
  const items = splitItems(text);
  if (items.length === 0) {
    return [{ kind: "prompt", text: prompt }];
  }
  return [
    {
      kind: "list",
      list: {
        style,
        items: items.map((item) => ({
          text: withLeadIn ? leadInItem(item) : item,
        })),
      },
    },
  ];
}

function attitudeBlocks(
  input: TpcasttInput,
  labels: TpcasttLabels
): DocBlock[] {
  const tones = parseTones(input.attitude);
  const blocks: DocBlock[] = [];
  if (tones.length === 0) {
    blocks.push({ kind: "prompt", text: labels.steps.attitude.prompt });
  } else {
    blocks.push({
      kind: "fields",
      fields: [
        { label: labels.toneLabel, value: joinSeries(tones, labels.and) },
      ],
    });
  }
  if (tidy(input.attitudeEvidence) !== "") {
    blocks.push(...paragraphsOrPrompt(input.attitudeEvidence, ""));
  }
  return blocks;
}

/** The seven steps as a document, plus a thesis starter built from the notes. */
export function buildTpcastt(
  input: TpcasttInput,
  options: TpcasttOptions,
  labels: TpcasttLabels
): DocumentModel {
  const title = stripEnd(input.title);
  const poet = stripEnd(input.poet);
  const prompts = (step: TpcasttStep) => labels.steps[step].prompt;
  const filled = (text: string) => tidy(text) !== "";
  const sections: DocSection[] = [
    stepSection(
      "title",
      labels,
      paragraphsOrPrompt(input.predict, prompts("title")),
      filled(input.predict),
      options
    ),
    stepSection(
      "paraphrase",
      labels,
      paragraphsOrPrompt(input.paraphrase, prompts("paraphrase")),
      filled(input.paraphrase),
      options
    ),
    stepSection(
      "connotation",
      labels,
      listBlocks(input.connotation, prompts("connotation"), true, "bullet"),
      filled(input.connotation),
      options
    ),
    stepSection(
      "attitude",
      labels,
      attitudeBlocks(input, labels),
      parseTones(input.attitude).length > 0,
      options
    ),
    stepSection(
      "shifts",
      labels,
      listBlocks(input.shifts, prompts("shifts"), true, "decimal"),
      filled(input.shifts),
      options
    ),
    stepSection(
      "titleAgain",
      labels,
      paragraphsOrPrompt(input.titleAgain, prompts("titleAgain")),
      filled(input.titleAgain),
      options
    ),
    stepSection(
      "theme",
      labels,
      paragraphsOrPrompt(input.theme, prompts("theme")),
      filled(input.theme),
      options
    ),
  ];
  const ready = filled(input.theme) && title !== "";
  const frames = ready
    ? buildTpcasttThesis(input, labels, labels.thesisFrames)
    : [];
  sections.push({
    heading: labels.thesisHeading,
    blocks:
      frames.length === 0
        ? [{ kind: "prompt", text: labels.thesisPrompt }]
        : [
            { kind: "guidance", text: labels.thesisGuidance },
            ...frames.map(
              (frame): DocBlock => ({ kind: "paragraph", text: frame })
            ),
          ],
  });
  return {
    title: title === "" ? labels.docTitleBlank : t(labels.docTitle, { title }),
    subtitle: poet === "" ? labels.subtitleBlank : t(labels.subtitle, { poet }),
    sections,
  };
}
